import { v4 as uuidv4 } from 'uuid';
import { mockOrders as defaultMockOrders, mockOrderItems } from '../mock/orders';
import { mockFeeCategories as defaultMockFeeCategories } from '../mock/feeCategories';
import { Order, FeeCategory } from '../types';

interface CartItem {
  id: string;
  fee: {
    id: string;
    name: string;
    categoryId: string;
    eventDate?: string;
    termsAndConditions?: {
      title: string;
      content: string;
      requiresSignature: boolean;
    };
  };
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  pricingTier: string;
  partialPaymentAmount?: number;
  questionnaireResponses?: any;
  signatureData?: {
    accepted: boolean;
    signedBy: string;
    signedAt: Date;
    drawnSignature?: string;
  };
}

interface PurchaseData {
  userId?: string;
  guestEmail?: string;
  guestFirstName?: string;
  guestLastName?: string;
  subtotal: number;
  tax: number;
  total: number;
  paymentMethod: string;
  items: CartItem[];
}

interface Purchase {
  id: string;
  user_id?: string;
  confirmation_number: string;
  guest_email?: string;
  guest_first_name?: string;
  guest_last_name?: string;
  subtotal: number;
  tax: number;
  total: number;
  payment_method: string;
  status: string;
  created_at: string;
  updated_at: string;
  purchase_items: PurchaseItem[];
}

interface PurchaseItem {
  id: string;
  purchase_id: string;
  fee_id: string;
  fee_name: string;
  fee_category: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  pricing_tier: string;
  partial_payment_amount?: number;
  questionnaire_responses?: any;
  created_at: string;
  tickets?: Ticket[];
  signed_agreements?: any[];
}

interface Ticket {
  id: string;
  purchase_item_id: string;
  purchase_id: string;
  ticket_number: string;
  qr_code_data: string;
  event_name: string;
  event_date: string;
  ticket_type: string;
  status: string;
  used_at?: string;
  created_at: string;
}

const PURCHASES_STORAGE_KEY = 'mock_purchases';

const getPurchasesFromStorage = (): Purchase[] => {
  try {
    const stored = localStorage.getItem(PURCHASES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to load purchases from storage:', error);
    return [];
  }
};

const savePurchasesToStorage = (purchases: Purchase[]) => {
  try {
    localStorage.setItem(PURCHASES_STORAGE_KEY, JSON.stringify(purchases));
  } catch (error) {
    console.error('Failed to save purchases to storage:', error);
  }
};

export const generateConfirmationNumber = (): string => {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `${timestamp}-${random}`;
};

export const generateTicketNumber = (): string => {
  return `TKT-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
};

export const createPurchase = async (purchaseData: PurchaseData) => {
  await new Promise(resolve => setTimeout(resolve, 100));

  const confirmationNumber = generateConfirmationNumber();
  const purchaseId = uuidv4();
  const now = new Date().toISOString();

  const purchaseItems: PurchaseItem[] = purchaseData.items.map((item) => {
    const purchaseItemId = uuidv4();
    const purchaseItem: PurchaseItem = {
      id: purchaseItemId,
      purchase_id: purchaseId,
      fee_id: item.fee.id,
      fee_name: item.fee.name,
      fee_category: item.fee.categoryId,
      quantity: item.quantity,
      unit_price: item.unitPrice,
      total_price: item.totalPrice,
      pricing_tier: item.pricingTier,
      partial_payment_amount: item.partialPaymentAmount,
      questionnaire_responses: item.questionnaireResponses || null,
      created_at: now,
      tickets: [],
      signed_agreements: [],
    };

    if (item.signatureData && item.fee.termsAndConditions) {
      purchaseItem.signed_agreements = [{
        id: uuidv4(),
        purchase_item_id: purchaseItemId,
        terms_title: item.fee.termsAndConditions.title,
        terms_content: item.fee.termsAndConditions.content,
        signed_by: item.signatureData.signedBy,
        signed_at: item.signatureData.signedAt.toISOString(),
        signature_image: item.signatureData.drawnSignature,
        created_at: now,
      }];
    }

    if (item.fee.categoryId === '5') {
      const tickets: Ticket[] = [];
      for (let i = 0; i < item.quantity; i++) {
        const ticketNumber = generateTicketNumber();
        const qrData = `${ticketNumber}:${purchaseId}:${item.fee.id}`;

        tickets.push({
          id: uuidv4(),
          purchase_item_id: purchaseItemId,
          purchase_id: purchaseId,
          ticket_number: ticketNumber,
          qr_code_data: qrData,
          event_name: item.fee.name,
          event_date: item.fee.eventDate || 'TBD',
          ticket_type: item.questionnaireResponses?.ticketType || item.pricingTier,
          status: 'active',
          created_at: now,
        });
      }
      purchaseItem.tickets = tickets;
    }

    return purchaseItem;
  });

  const purchase: Purchase = {
    id: purchaseId,
    user_id: purchaseData.userId,
    confirmation_number: confirmationNumber,
    guest_email: purchaseData.guestEmail,
    guest_first_name: purchaseData.guestFirstName,
    guest_last_name: purchaseData.guestLastName,
    subtotal: purchaseData.subtotal,
    tax: purchaseData.tax,
    total: purchaseData.total,
    payment_method: purchaseData.paymentMethod,
    status: 'completed',
    created_at: now,
    updated_at: now,
    purchase_items: purchaseItems,
  };

  const purchases = getPurchasesFromStorage();
  purchases.unshift(purchase);
  savePurchasesToStorage(purchases);

  return {
    purchase,
    purchaseItems,
    confirmationNumber,
  };
};

const convertMockOrderToPurchase = (order: Order, feeCategories: FeeCategory[]): Purchase => {
  const orderItems = order.items || mockOrderItems.filter(item => item.orderId === order.id);

  const purchaseItems: PurchaseItem[] = orderItems.map(item => {
    const categoryName = feeCategories.find(c => c.name === item.feeCategory)?.id || '6';
    const isEventTicket = categoryName === '5';
    const tickets: Ticket[] = isEventTicket
      ? Array.from({ length: item.quantity }).map((_, i) => {
          const ticketNumber = `TKT-${order.orderNumber.replace(/\D/g, '').slice(-4)}-${String(i + 1).padStart(3, '0')}`;
          return {
            id: `${item.id}-tkt-${i + 1}`,
            purchase_item_id: item.id,
            purchase_id: order.id,
            ticket_number: ticketNumber,
            qr_code_data: `${ticketNumber}:${order.id}:${item.feeId}`,
            event_name: item.feeName,
            event_date: item.createdAt.toISOString(),
            ticket_type: item.pricingTier,
            status: 'active',
            created_at: item.createdAt.toISOString(),
          };
        })
      : [];
    return {
      id: item.id,
      purchase_id: order.id,
      fee_id: item.feeId,
      fee_name: item.feeName,
      fee_category: categoryName,
      quantity: item.quantity,
      unit_price: item.unitPrice,
      total_price: item.totalPrice,
      pricing_tier: item.pricingTier,
      partial_payment_amount: item.partialPaymentAmount,
      questionnaire_responses: item.questionnaireResponses,
      created_at: item.createdAt.toISOString(),
      tickets,
      signed_agreements: item.signatureData ? [{
        id: uuidv4(),
        purchase_item_id: item.id,
        terms_title: 'Terms and Conditions',
        terms_content: '',
        signed_by: item.signatureData.signedBy,
        signed_at: item.signatureData.signedAt.toISOString(),
        created_at: item.createdAt.toISOString(),
      }] : [],
    };
  });

  return {
    id: order.id,
    user_id: order.userId,
    confirmation_number: order.orderNumber,
    subtotal: order.subtotal,
    tax: order.tax,
    total: order.total,
    payment_method: order.paymentMethod || 'N/A',
    status: order.paymentStatus,
    created_at: order.createdAt.toISOString(),
    updated_at: order.completedAt?.toISOString() || order.createdAt.toISOString(),
    purchase_items: purchaseItems,
  };
};

export const getPurchasesByUser = async (userId: string, orders?: Order[], feeCategories?: FeeCategory[]) => {
  await new Promise(resolve => setTimeout(resolve, 100));

  const storagePurchases = getPurchasesFromStorage();
  const userStoragePurchases = storagePurchases.filter(p => p.user_id === userId);

  const mockOrders = orders || defaultMockOrders;
  const mockFeeCategories = feeCategories || defaultMockFeeCategories;

  const userMockOrders = mockOrders.filter(
    order => order.userId === userId && order.paymentStatus === 'completed'
  );
  const mockPurchases = userMockOrders.map(order => convertMockOrderToPurchase(order, mockFeeCategories));

  const allPurchases = [...userStoragePurchases, ...mockPurchases];
  allPurchases.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return allPurchases;
};

export const getTicketsByPurchase = async (purchaseId: string) => {
  await new Promise(resolve => setTimeout(resolve, 100));

  const purchases = getPurchasesFromStorage();
  const purchase = purchases.find(p => p.id === purchaseId);

  if (!purchase) {
    return [];
  }

  const tickets: Ticket[] = [];
  purchase.purchase_items.forEach(item => {
    if (item.tickets) {
      tickets.push(...item.tickets);
    }
  });

  return tickets;
};
